// 우편 적용(docs/PARTY-TRADE-PRD.md 1.1·3.6): 서버 우편함에서 받은(claim) 골드·장비를 장비 장부(gear.v1)에 넣고 저장까지 끝낸다.
// 연산 id = mk:m<우편 id>. 같은 우편을 다시 받아도(ack 전 재전송·두 탭) 장부가 같은 id를 한 번만 적용한다.
// 호출 순서(UI 레인 src/market/mailClient.ts): claim → applyMail → (true일 때만) ack.
import type { GearItem } from '../shared/gear';
import type { GearCode } from '../shared/gearLedger';
import type { GearStore } from './gear';
import type { ProfileStore } from './profile';

/** 서버 우편 한 통(GET /api/mail·POST /api/mail/claim 응답의 MailView 중 필요한 것) */
export interface MailLike {
  id: number;
  kind: 'gold' | 'item';
  gold: number;
  item: GearItem | null;
}
/** applyMail이 읽는 게임 쪽(GameSim이 그대로 맞는다) */
export interface MailGame {
  readonly gear: Pick<GearStore, 'applyMarket'>;
  readonly profile: Pick<ProfileStore, 'mode'>;
}

/** 마지막 적용 결과 코드(UI 문구용: bag_full이면 "가방을 비운 뒤 받기") */
export let lastMailCode: GearCode | 'bad_mail' = 'ok';

/** 우편 id → 장부 연산 id(서버 거래 id에서 결정적) */
export const mailOpId = (id: number): string => 'mk:m' + id;

/**
 * 우편 한 통을 장부에 넣고 저장 확정까지 기다린다. true = 이제 ack해도 된다(새로 넣었거나 이미 들어 있음).
 * false = ack하지 말 것(가방 가득·저장 실패·다른 탭이 거래 권한·잘못된 우편) — 서버가 다음 접속에 다시 준다.
 */
export async function applyMail(game: MailGame, mail: MailLike): Promise<boolean> {
  if (!mail || !Number.isSafeInteger(mail.id) || mail.id <= 0) { lastMailCode = 'bad_mail'; return false; }
  const id = mailOpId(mail.id);
  let code: GearCode;
  if (mail.kind === 'gold') {
    if (!Number.isSafeInteger(mail.gold) || mail.gold <= 0) { lastMailCode = 'bad_mail'; return false; }
    // 골드가 움직이는 연산은 이 탭이 상점 장부 writer일 때만(다른 탭과 잔액이 엇갈리지 않게 — gearCommand와 같은 규칙)
    code = game.profile.mode !== 'ok' ? 'readonly' : game.gear.applyMarket({ id, t: 'mkGold', d: mail.gold });
  } else if (mail.kind === 'item' && mail.item) {
    code = game.gear.applyMarket({ id, t: 'mkIn', item: mail.item });
  } else { lastMailCode = 'bad_mail'; return false; }
  lastMailCode = code;
  // 저장은 applyMarket 안에서 동기(localStorage)로 끝난다. 호출자가 await로 다음 단계(ack)를 이을 수 있게 비동기 함수로 둔다
  return code === 'ok' || code === 'duplicate';
}
